// SHARE-7 (docs/specs/share-7.md §5.2): the browser-side Microsoft Graph people-search
// source that lets the share picker find ANY org member, not just platform users who have
// signed in before. This call is made from the BROWSER, never the backend — the deployed API
// Lambda runs in a zero-egress VPC (ADR-002; the M8 lesson recorded in docs/MILESTONES.md and
// `backend/src/scribe/api/auth.py::gateway_verified_claims`'s docstring: an in-VPC outbound
// call just hangs until the Lambda times out). The browser has unrestricted egress and Graph
// serves CORS to SPA tokens, so `createGraphDirectorySource` talks to
// https://graph.microsoft.com directly using a delegated token acquired separately from the
// Scribe API scope (MSAL cannot mix resources in one `acquireTokenSilent` call — see
// `graphPeopleSearchRequest` in `./authConfig`).
//
// Never log a Graph response or the query string — names/emails read from Graph are PII, same
// rule as the platform directory (see queries.ts/mock.ts's existing "counts only" convention).
import { useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { graphPeopleSearchRequest } from './authConfig';
import { isMock, mock } from './mock';
import { isLocalAuth } from './localAuth';
import type { ProvisionUserRequest } from '@scribe/core/types';

export type ExternalDirectorySource = (q: string) => Promise<ProvisionUserRequest[]>;

/** Thrown when Graph consent hasn't been granted yet — UserPicker turns this into the
 *  inline "enable org-wide search" affordance instead of an error state. */
export class GraphConsentRequiredError extends Error {}

interface GraphUser {
  id: string;
  displayName: string | null;
  mail: string | null;
  userPrincipalName: string | null;
}

interface GraphUserSearchResponse {
  value: GraphUser[];
}

function toProvisionRequest(u: GraphUser): ProvisionUserRequest | undefined {
  const email = u.mail ?? u.userPrincipalName;
  if (!email) return undefined; // unshareable-by-email service objects — drop
  return {
    entra_oid: u.id,
    display_name: u.displayName ?? email,
    email,
  };
}

/**
 * Browser-side Graph `GET /users` `$search` typeahead bound to a given MSAL instance.
 * Acquires a delegated `User.ReadBasic.All` token (SEPARATE resource from the Scribe API
 * scope) via `acquireTokenSilent`, mirroring `apiClient.ts::createTokenProvider` EXCEPT that
 * `InteractionRequiredAuthError` throws `GraphConsentRequiredError` here rather than kicking
 * off a redirect — a background typeahead must never yank the user through one.
 */
export function createGraphDirectorySource(
  instance: IPublicClientApplication,
): ExternalDirectorySource {
  return async (q: string): Promise<ProvisionUserRequest[]> => {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) throw new GraphConsentRequiredError('No signed-in account for Graph search.');

    let accessToken: string;
    try {
      const result = await instance.acquireTokenSilent({ ...graphPeopleSearchRequest, account });
      accessToken = result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        throw new GraphConsentRequiredError('Graph people-search consent required.');
      }
      throw err;
    }

    // `$search` is an advanced query — it REQUIRES `ConsistencyLevel: eventual` +
    // `$count=true`; skip `$orderby` (advanced-query + `$orderby` combinations are rejected
    // by Graph) and sort client-side instead. Strip `"` from `q` before interpolation (Graph
    // `$search` quoting) and URL-encode the whole param value.
    const cleaned = q.replace(/"/g, '');
    const search = `"displayName:${cleaned}" OR "mail:${cleaned}"`;
    const url =
      'https://graph.microsoft.com/v1.0/users' +
      `?$search=${encodeURIComponent(search)}` +
      '&$select=id,displayName,mail,userPrincipalName' +
      '&$top=20&$count=true';

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ConsistencyLevel: 'eventual',
      },
    });

    if (response.status === 401 || response.status === 403) {
      // Token minted but tenant policy blocks the scope — same UX as no consent at all.
      throw new GraphConsentRequiredError('Graph denied the people-search scope.');
    }
    if (!response.ok) {
      throw new Error('Microsoft Graph people search failed.');
    }

    const body = (await response.json()) as GraphUserSearchResponse;
    const people = body.value
      .map(toProvisionRequest)
      .filter((p): p is ProvisionUserRequest => p !== undefined);
    people.sort((a, b) => a.display_name.localeCompare(b.display_name));
    return people;
  };
}

/** Interactive Graph consent grant — `acquireTokenPopup`, NOT `acquireTokenRedirect`, so the
 *  open ShareDialog's in-memory state (the picker query, the selected candidate) survives the
 *  consent prompt instead of being lost to a full-page navigation. */
export async function requestGraphConsent(instance: IPublicClientApplication): Promise<void> {
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  await instance.acquireTokenPopup(
    account ? { ...graphPeopleSearchRequest, account } : { ...graphPeopleSearchRequest },
  );
}

/**
 * Mode-aware hook selecting the second (external) directory source for the share picker:
 *   - `dev:mock`       → the mock external-directory fixture (`mock.searchExternalDirectory`)
 *     so the merged UX is demoable/testable offline; no consent step exists in mock mode.
 *   - `dev:local-auth` → `undefined` (platform-only search; no real Entra app registration).
 *   - real Entra       → a Graph source bound to the live MSAL instance, plus a consent
 *     requester the picker's "Search your whole organization" affordance calls.
 */
export function useExternalDirectory(): {
  source: ExternalDirectorySource | undefined;
  requestConsent: (() => Promise<void>) | undefined;
} {
  const { instance } = useMsal();

  return useMemo(() => {
    if (isMock()) {
      return {
        source: (q: string) => mock.searchExternalDirectory(q),
        requestConsent: undefined,
      };
    }
    if (isLocalAuth()) {
      return { source: undefined, requestConsent: undefined };
    }
    return {
      source: createGraphDirectorySource(instance),
      requestConsent: () => requestGraphConsent(instance),
    };
  }, [instance]);
}
